import React, { useEffect, useState, Children } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { ChevronDown, MapPin, Clock, Phone, Instagram } from 'lucide-react';
export function NatuCafePage() {
  const {
    scrollY
  } = useScroll();
  const heroY = useTransform(scrollY, [0, 1000], [0, 300]);
  const heroOpacity = useTransform(scrollY, [0, 500], [1, 0]);
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    // Solo actualizamos el estado cuando cruza el umbral (no en cada
    // pixel de scroll) y usamos passive listener — evita re-renders
    // constantes de esta página (que tiene mucho framer-motion) y
    // hace el scroll mucho más fluido en móvil.
    const handleScroll = () => {
      const scrolled = window.scrollY > 50;
      setIsScrolled(prev => prev === scrolled ? prev : scrolled);
    };
    window.addEventListener('scroll', handleScroll, {
      passive: true
    });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const scrollTo = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const fadeUpVariants = {
    hidden: {
      opacity: 0,
      y: 40
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 1,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };
  const staggerContainer = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  return <div className="bg-[#0B0B0B] min-h-screen text-[#F5F0EB] font-sans selection:bg-[#C6A969] selection:text-[#0B0B0B] overflow-x-hidden relative" data-id="element-7226" data-mp="29uldpptuarfe">
      {/* Film Grain Overlay */}
      <div className="pointer-events-none fixed inset-0 z-50 opacity-[0.03]" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
    }} data-id="element-7227" data-mp="fh2aq0h1igmp" />

      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-40 transition-all duration-700 ${isScrolled ? 'bg-[#0B0B0B]/90 backdrop-blur-md py-4' : 'bg-transparent py-8'}`} data-id="element-7228" data-mp="1rseaf7v46dq6">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between" data-id="element-7229" data-mp="26nc17wam6v55">
          <div className="font-serif text-2xl md:text-3xl tracking-wider font-medium text-[#F5F0EB]" data-id="element-7230" data-mp="21ohj6wkh1am3">
            NatuCafé
          </div>
          <div className="hidden md:flex items-center gap-10 text-xs tracking-[0.2em] uppercase text-[#F5F0EB]/70" data-id="element-7231" data-mp="2bfvfywl1fwbj">
            <button onClick={() => scrollTo('origen')} className="hover:text-[#C6A969] transition-colors" data-id="element-7232" data-mp="2ph7ur4ogprvu">
              Origen
            </button>
            <button onClick={() => scrollTo('menu')} className="hover:text-[#C6A969] transition-colors" data-id="element-7233" data-mp="342lq35lcwval">
              Menú
            </button>
            <button onClick={() => scrollTo('experiencia')} className="hover:text-[#C6A969] transition-colors" data-id="element-7234" data-mp="2u578h2np60gp">
              Experiencia
            </button>
            <button onClick={() => scrollTo('contacto')} className="hover:text-[#C6A969] transition-colors" data-id="element-7235" data-mp="2lbu7reje5uk2">
              Contacto
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative h-screen w-full overflow-hidden flex items-center justify-center" data-id="element-7236" data-mp="2bc3cvbqx5djg">
        <motion.div style={{
        y: heroY,
        opacity: heroOpacity
      }} className="absolute inset-0 w-full h-full" data-id="element-7237" data-mp="2gb6esnblr3n7">
          <div className="absolute inset-0 bg-black/60 z-10" data-id="element-7238" data-mp="26elbq7j582m0" />
          <img src="https://images.unsplash.com/photo-1447933601403-0c6688de566e?w=1920&q=80" alt="Premium Coffee" className="w-full h-full object-cover" data-id="element-7239" data-mp="1zcg37jx9k2ts" />
        </motion.div>

        <div className="relative z-20 max-w-5xl mx-auto px-6 text-center mt-20" data-id="element-7240" data-mp="1pl21r6lr4sy0">
          <motion.div initial="hidden" animate="visible" variants={staggerContainer} className="space-y-8" data-id="element-7241" data-mp="1knse9whxarct">
            <motion.h1 variants={fadeUpVariants} className="font-serif text-5xl md:text-7xl lg:text-8xl text-[#F5F0EB] leading-[1.1] tracking-wide" data-id="element-7242" data-mp="1bmc2bkbgmh6h">
              Donde el café <br className="hidden md:block" data-id="element-7243" data-mp="11o63xcov3t6d" />
              se convierte en arte
            </motion.h1>
            <motion.p variants={fadeUpVariants} className="font-sans font-light text-lg md:text-xl text-[#F5F0EB]/80 max-w-2xl mx-auto leading-relaxed tracking-wide" data-id="element-7244" data-mp="3110cdewkvn1c">
              Cultivado a 1,700 metros en Los Santos, Costa Rica.{' '}
              <br className="hidden md:block" data-id="element-7245" data-mp="34hda61dyopvy" />
              Tostado con maestría. Servido con devoción.
            </motion.p>
            <motion.div variants={fadeUpVariants} className="pt-8" data-id="element-7246" data-mp="3daztut4wa2os">
              <button onClick={() => scrollTo('menu')} className="px-10 py-4 border border-[#C6A969] text-[#C6A969] text-xs uppercase tracking-[0.2em] hover:bg-[#C6A969] hover:text-[#0B0B0B] transition-all duration-500" data-id="element-7247" data-mp="3ddueaknwrni7">
                Descubrir la experiencia
              </button>
            </motion.div>
          </motion.div>
        </div>

        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 2,
        duration: 1
      }} className="absolute bottom-12 left-1/2 -translate-x-1/2 z-20" data-id="element-7248" data-mp="2un38tu71kh93">
          <motion.div animate={{
          y: [0, 10, 0]
        }} transition={{
          repeat: Infinity,
          duration: 2,
          ease: 'easeInOut'
        }} data-id="element-7249" data-mp="evp47t0jp7z5">
            <ChevronDown className="w-6 h-6 text-[#F5F0EB]/40" data-id="element-7250" data-mp="3v9tze6q6d0jy" />
          </motion.div>
        </motion.div>
      </section>

      {/* Origin Section */}
      <section id="origen" className="py-32 md:py-48 relative" data-id="element-7251" data-mp="3q84n6nr7ugua">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-7252" data-mp="32loe9iv7l9jl">
          <div className="w-full h-px bg-[#C6A969]/20 mb-32" data-id="element-7253" data-mp="2xljyxw61ekuy" />

          <div className="grid md:grid-cols-2 gap-16 md:gap-24 items-center" data-id="element-7254" data-mp="2xnd4t98tt11w">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={staggerContainer} className="order-2 md:order-1" data-id="element-7255" data-mp="3cb7z2u2hfdh3">
              <motion.h2 variants={fadeUpVariants} className="font-serif text-4xl md:text-5xl lg:text-6xl mb-8 leading-tight" data-id="element-7256" data-mp="2eu98fg484a3l">
                Nacido en las montañas <br data-id="element-7257" data-mp="z4rra3mtbeaa" /> de Los Santos
              </motion.h2>
              <motion.div variants={fadeUpVariants} className="space-y-6 font-sans font-light text-[#F5F0EB]/70 leading-[1.9] text-lg" data-id="element-7258" data-mp="kf3sb923srpq">
                <p data-id="element-7259" data-mp="pbkdpvhx4rz4">
                  A 1,700 metros sobre el nivel del mar, donde la neblina abraza
                  las laderas y el suelo volcánico nutre cada raíz, nace nuestro
                  café. Un entorno privilegiado que confiere a cada grano una
                  acidez brillante y notas complejas.
                </p>
                <p data-id="element-7260" data-mp="183n1dqsz98iv">
                  NatuCafé no es solo una cafetería; es el tributo a
                  generaciones de caficultores que han perfeccionado el arte del
                  cultivo. Cada taza que servimos es el resultado de un proceso
                  meticuloso, desde la recolección manual hasta el tueste
                  artesanal.
                </p>
              </motion.div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true,
            margin: '-100px'
          }} transition={{
            duration: 1.2,
            ease: [0.25, 0.1, 0.25, 1]
          }} className="order-1 md:order-2 aspect-[3/4] md:aspect-[4/5] overflow-hidden" data-id="element-7261" data-mp="jcyauc3s6ml9">
              <img src="https://images.unsplash.com/photo-1611854779393-1b2da9d400fe?w=800&q=80" alt="Coffee Plantation" className="w-full h-full object-cover" data-id="element-7262" data-mp="tc5l2qf7y145" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Menu Section */}
      <section id="menu" className="py-32 md:py-48 bg-[#111111]" data-id="element-7263" data-mp="1tciu7u27kdln">
        <div className="max-w-5xl mx-auto px-6 md:px-12" data-id="element-7264" data-mp="1te285e0e6ck9">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUpVariants} className="text-center mb-24" data-id="element-7265" data-mp="1yblrgsby5fj2">
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-6" data-id="element-7266" data-mp="2cbd870tula0r">
              Nuestra Carta
            </h2>
            <p className="font-sans font-light text-[#F5F0EB]/60 tracking-widest uppercase text-xs" data-id="element-7267" data-mp="2h8dd38eyo6qa">
              Una selección curada para paladares exigentes
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-x-24 gap-y-20" data-id="element-7268" data-mp="2qy6ifroifpm3">
            {/* Category 1 */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={staggerContainer} data-id="element-7269" data-mp="2v2e9hvifxhnh">
              <motion.h3 variants={fadeUpVariants} className="text-xs font-sans uppercase tracking-[0.3em] text-[#C6A969] mb-8 pb-4 border-b border-[#C6A969]/20" data-id="element-7270" data-mp="3isbynwt6iays">
                Café de Origen
              </motion.h3>
              <div className="space-y-8" data-id="element-7271" data-mp="3skhpv4blwocx">
                {[{
                name: 'Espresso Clásico',
                price: '₡1,800',
                desc: 'Intenso, notas a cacao oscuro y nuez tostada.'
              }, {
                name: 'Americano',
                price: '₡2,000',
                desc: 'Equilibrado, cuerpo medio y final limpio.'
              }, {
                name: 'Cappuccino',
                price: '₡2,500',
                desc: 'Espresso perfecto con leche texturizada sedosa.'
              }, {
                name: 'Latte',
                price: '₡2,500',
                desc: 'Suave y cremoso, ideal para resaltar el dulzor natural.'
              }].map((item, i) => <motion.div key={i} variants={fadeUpVariants} className="group" data-id="element-7272" data-mp="pgbi4obtd9kf" data-mpi={i}>
                    <div className="flex justify-between items-baseline mb-2" data-id="element-7273" data-mp="1h7hmxi2hbvg1">
                      <h4 className="font-serif text-xl text-[#F5F0EB]" data-id="element-7274" data-mp="1d5e8ckkikaqj">
                        {item.name}
                      </h4>
                      <span className="font-sans font-light text-[#F5F0EB]/80 text-sm" data-id="element-7275" data-mp="1858fj12ici6v">
                        {item.price}
                      </span>
                    </div>
                    <p className="font-sans font-light text-[#F5F0EB]/50 text-sm leading-relaxed" data-id="element-7276" data-mp="3sx1urm1wikv3">
                      {item.desc}
                    </p>
                  </motion.div>)}
              </div>
            </motion.div>

            {/* Category 2 */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={staggerContainer} data-id="element-7277" data-mp="3udpbzzzchv3q">
              <motion.h3 variants={fadeUpVariants} className="text-xs font-sans uppercase tracking-[0.3em] text-[#C6A969] mb-8 pb-4 border-b border-[#C6A969]/20" data-id="element-7278" data-mp="3lboldlyma7b1">
                Métodos de Preparación
              </motion.h3>
              <div className="space-y-8" data-id="element-7279" data-mp="2xo80h4doleu4">
                {[{
                name: 'Pour Over V60',
                price: '₡3,200',
                desc: 'Resalta la acidez brillante y notas florales.'
              }, {
                name: 'Chemex',
                price: '₡3,500',
                desc: 'Extracción limpia, cuerpo ligero y elegante.'
              }, {
                name: 'Aeropress',
                price: '₡3,000',
                desc: 'Cuerpo denso, sabores intensos y concentrados.'
              }, {
                name: 'Cold Brew',
                price: '₡3,200',
                desc: 'Maceración de 18 horas, refrescante y sin amargor.'
              }].map((item, i) => <motion.div key={i} variants={fadeUpVariants} className="group" data-id="element-7280" data-mp="2n2l2xr4qdrte" data-mpi={i}>
                    <div className="flex justify-between items-baseline mb-2" data-id="element-7281" data-mp="31qfvn7iqnw6h">
                      <h4 className="font-serif text-xl text-[#F5F0EB]" data-id="element-7282" data-mp="2wtfpkwlw2tfd">
                        {item.name}
                      </h4>
                      <span className="font-sans font-light text-[#F5F0EB]/80 text-sm" data-id="element-7283" data-mp="3apjgafxk0jn3">
                        {item.price}
                      </span>
                    </div>
                    <p className="font-sans font-light text-[#F5F0EB]/50 text-sm leading-relaxed" data-id="element-7284" data-mp="143df1eia2evq">
                      {item.desc}
                    </p>
                  </motion.div>)}
              </div>
            </motion.div>

            {/* Category 3 */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={staggerContainer} data-id="element-7285" data-mp="1ir89p0fuitbg">
              <motion.h3 variants={fadeUpVariants} className="text-xs font-sans uppercase tracking-[0.3em] text-[#C6A969] mb-8 pb-4 border-b border-[#C6A969]/20" data-id="element-7286" data-mp="v2yuihd513ta">
                Especialidades de la Casa
              </motion.h3>
              <div className="space-y-8" data-id="element-7287" data-mp="26k6vesoks1sp">
                {[{
                name: 'Café de la Casa',
                price: '₡3,800',
                desc: 'Nuestra receta secreta. Una experiencia sensorial única.',
                highlight: true
              }, {
                name: 'Affogato',
                price: '₡3,500',
                desc: 'Espresso caliente sobre helado artesanal de vainilla.'
              }, {
                name: 'Mocha Artesanal',
                price: '₡3,200',
                desc: 'Espresso, leche texturizada y cacao puro al 70%.'
              }].map((item, i) => <motion.div key={i} variants={fadeUpVariants} className="group" data-id="element-7288" data-mp="339sk0jy4gwkf" data-mpi={i}>
                    <div className="flex justify-between items-baseline mb-2" data-id="element-7289" data-mp="2ydtweykmbil2">
                      <h4 className={`font-serif text-xl ${item.highlight ? 'text-[#C6A969]' : 'text-[#F5F0EB]'}`} data-id="element-7290" data-mp="11h4nsujwb9e5">
                        {item.name}
                      </h4>
                      <span className={`font-sans font-light text-sm ${item.highlight ? 'text-[#C6A969]' : 'text-[#F5F0EB]/80'}`} data-id="element-7291" data-mp="1kffwpxl82qh8">
                        {item.price}
                      </span>
                    </div>
                    <p className="font-sans font-light text-[#F5F0EB]/50 text-sm leading-relaxed" data-id="element-7292" data-mp="1bndy84uprk9l">
                      {item.desc}
                    </p>
                  </motion.div>)}
              </div>
            </motion.div>

            {/* Category 4 */}
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={staggerContainer} data-id="element-7293" data-mp="1pdr9ag2wjf35">
              <motion.h3 variants={fadeUpVariants} className="text-xs font-sans uppercase tracking-[0.3em] text-[#C6A969] mb-8 pb-4 border-b border-[#C6A969]/20" data-id="element-7294" data-mp="iy721ciqf6ri">
                Repostería Artesanal
              </motion.h3>
              <div className="space-y-8" data-id="element-7295" data-mp="6hyy4zmngz3q">
                {[{
                name: 'Croissant de Almendra',
                price: '₡2,800',
                desc: 'Horneado diariamente, relleno de crema de almendras.'
              }, {
                name: 'Tarta de Chocolate',
                price: '₡3,500',
                desc: 'Chocolate oscuro belga, textura sedosa y base crujiente.'
              }, {
                name: 'Cheesecake',
                price: '₡3,200',
                desc: 'Estilo neoyorquino, con coulis de frutos rojos de la zona.'
              }].map((item, i) => <motion.div key={i} variants={fadeUpVariants} className="group" data-id="element-7296" data-mp="3071la1zqr3fk" data-mpi={i}>
                    <div className="flex justify-between items-baseline mb-2" data-id="element-7297" data-mp="2fn19s09tns3f">
                      <h4 className="font-serif text-xl text-[#F5F0EB]" data-id="element-7298" data-mp="2jn1ycdqmxjo7">
                        {item.name}
                      </h4>
                      <span className="font-sans font-light text-[#F5F0EB]/80 text-sm" data-id="element-7299" data-mp="2onzc8e4m3rjw">
                        {item.price}
                      </span>
                    </div>
                    <p className="font-sans font-light text-[#F5F0EB]/50 text-sm leading-relaxed" data-id="element-7300" data-mp="2yjkdub5rqpt4">
                      {item.desc}
                    </p>
                  </motion.div>)}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Experience / Ambience Section */}
      <section id="experiencia" className="relative py-48 md:py-64 overflow-hidden" data-id="element-7301" data-mp="3hak73okaqls7">
        <div className="absolute inset-0 w-full h-full" data-id="element-7302" data-mp="3m8vgxzalcwaf">
          <div className="absolute inset-0 bg-[#0B0B0B]/70 z-10" data-id="element-7303" data-mp="3m7ame9oa1dck" />
          <img src="https://images.unsplash.com/photo-1559305616-3f99cd43e353?w=1920&q=80" alt="NatuCafé Ambience" className="w-full h-full object-cover object-center" data-id="element-7304" data-mp="1amtaivavsu1x" />
        </div>

        <div className="relative z-20 max-w-4xl mx-auto px-6 text-center" data-id="element-7305" data-mp="10pydkgu9b1pr">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} data-id="element-7306" data-mp="vqbz642ozffd">
            <motion.h2 variants={fadeUpVariants} className="font-serif text-4xl md:text-5xl lg:text-6xl mb-10 leading-tight" data-id="element-7307" data-mp="1ofspmw6r4m8y">
              "Un espacio para conversaciones que importan"
            </motion.h2>
            <motion.p variants={fadeUpVariants} className="font-sans font-light text-lg md:text-xl text-[#F5F0EB]/80 leading-relaxed" data-id="element-7308" data-mp="2c42de3nizk2q">
              Diseñado para la pausa consciente. Sin distracciones, sin prisa.{' '}
              <br className="hidden md:block" data-id="element-7309" data-mp="3sybff1wr71q4" />
              Solo usted, una excelente taza de café y el momento presente.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Craftsmanship Section */}
      <section className="py-32 md:py-48" data-id="element-7310" data-mp="3jyewlgx1ox2z">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-7311" data-mp="3jzq15iybe8v5">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid md:grid-cols-3 gap-16 text-center" data-id="element-7312" data-mp="kgyhuu8xzb68">
            <motion.div variants={fadeUpVariants} className="space-y-6" data-id="element-7313" data-mp="6ps9e8noq377">
              <div className="font-serif text-5xl text-[#C6A969]" data-id="element-7314" data-mp="146r3xxp2dqpz">1,700m</div>
              <h4 className="font-sans uppercase tracking-[0.2em] text-sm text-[#F5F0EB]" data-id="element-7315" data-mp="zfis6v09ht9n">
                Altitud Perfecta
              </h4>
              <p className="font-sans font-light text-[#F5F0EB]/50 text-sm leading-relaxed max-w-xs mx-auto" data-id="element-7316" data-mp="16rp1xbrmn6up">
                Cultivado en las alturas de Los Santos, permitiendo una
                maduración lenta que concentra los azúcares naturales.
              </p>
            </motion.div>

            <motion.div variants={fadeUpVariants} className="space-y-6" data-id="element-7317" data-mp="1krhnksoaqwvt">
              <div className="font-serif text-5xl text-[#C6A969]" data-id="element-7318" data-mp="1ppt1pflmjtju">+30</div>
              <h4 className="font-sans uppercase tracking-[0.2em] text-sm text-[#F5F0EB]" data-id="element-7319" data-mp="1pnovgr096ige">
                Años de Tradición
              </h4>
              <p className="font-sans font-light text-[#F5F0EB]/50 text-sm leading-relaxed max-w-xs mx-auto" data-id="element-7320" data-mp="1zgdugmema3uu">
                El conocimiento transmitido de generación en generación,
                perfeccionando cada etapa del proceso.
              </p>
            </motion.div>

            <motion.div variants={fadeUpVariants} className="space-y-6" data-id="element-7321" data-mp="2dekk5f9ewxjf">
              <div className="font-serif text-5xl text-[#C6A969]" data-id="element-7322" data-mp="2dcradr3i5x77">Arte</div>
              <h4 className="font-sans uppercase tracking-[0.2em] text-sm text-[#F5F0EB]" data-id="element-7323" data-mp="dd46rzrq244w">
                Tostado Artesanal
              </h4>
              <p className="font-sans font-light text-[#F5F0EB]/50 text-sm leading-relaxed max-w-xs mx-auto" data-id="element-7324" data-mp="n2e3xu1faczv">
                Tostamos en pequeños lotes para garantizar que cada grano
                alcance su máximo potencial de sabor.
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer id="contacto" className="bg-[#0B0B0B] pt-20 pb-10 border-t border-[#C6A969]/20" data-id="element-7325" data-mp="s1h7tb5o2d69">
        <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-7326" data-mp="15z5t2k3nbmbu">
          <div className="grid md:grid-cols-3 gap-16 mb-24" data-id="element-7327" data-mp="113qhjyt422il">
            <div data-id="element-7328" data-mp="37smw1agtzz09">
              <h2 className="font-serif text-3xl mb-6" data-id="element-7329" data-mp="37s3g1zaqrkov">NatuCafé</h2>
              <p className="font-sans font-light text-[#F5F0EB]/50 text-sm leading-relaxed max-w-xs" data-id="element-7330" data-mp="32ttjplda6e1o">
                La máxima expresión del café de especialidad en Costa Rica.
              </p>
            </div>

            <div className="space-y-6" data-id="element-7331" data-mp="1nakpr9rgle9r">
              <h4 className="font-sans uppercase tracking-[0.2em] text-xs text-[#C6A969]" data-id="element-7332" data-mp="1nacefmazih0o">
                Visítenos
              </h4>
              <div className="space-y-4 font-sans font-light text-[#F5F0EB]/70 text-sm" data-id="element-7333" data-mp="19hmox7w8ytyt">
                <p className="flex items-start gap-3" data-id="element-7334" data-mp="1diybf4jtqz2s">
                  <MapPin className="w-4 h-4 mt-0.5 text-[#C6A969]" data-id="element-7335" data-mp="zk899ai5e97k" />
                  <span data-id="element-7336" data-mp="14k357sgs5ozh">
                    Los Santos, San José
                    <br data-id="element-7337" data-mp="14i9v236ykmmq" />
                    Costa Rica
                  </span>
                </p>
                <p className="flex items-start gap-3" data-id="element-7338" data-mp="qmob4afl0t16">
                  <Clock className="w-4 h-4 mt-0.5 text-[#C6A969]" data-id="element-7339" data-mp="un85j68rtijm" />
                  <span data-id="element-7340" data-mp="2kyvy91lnw5me">
                    Lunes a Sábado
                    <br data-id="element-7341" data-mp="2kze14h2txi3a" />
                    7:00 am - 6:00 pm
                  </span>
                </p>
              </div>
            </div>

            <div className="space-y-6" data-id="element-7342" data-mp="20a3ghjvxngpz">
              <h4 className="font-sans uppercase tracking-[0.2em] text-xs text-[#C6A969]" data-id="element-7343" data-mp="2091syfxqd1rd">
                Contacto
              </h4>
              <div className="space-y-4 font-sans font-light text-[#F5F0EB]/70 text-sm" data-id="element-7344" data-mp="1v8mdnitw1k6r">
                <p className="flex items-center gap-3" data-id="element-7345" data-mp="1qdgtaog4wdks">
                  <Phone className="w-4 h-4 text-[#C6A969]" data-id="element-7346" data-mp="1lb9gxxre6vhx" />
                  <span data-id="element-7347" data-mp="1hafqawmkf2lc">+506 8888-8888</span>
                </p>
                <p className="flex items-center gap-3" data-id="element-7348" data-mp="1hc0n6swcqxmd">
                  <Instagram className="w-4 h-4 text-[#C6A969]" data-id="element-7349" data-mp="1cjpkjia88xq4" />
                  <span data-id="element-7350" data-mp="17kw6nor134pf">@natucafe.cr</span>
                </p>
              </div>
            </div>
          </div>

          <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row justify-between items-center gap-4" data-id="element-7351" data-mp="12m1rcws9rya2">
            <p className="font-sans font-light text-[#F5F0EB]/30 text-xs" data-id="element-7352" data-mp="xmykd6sqbhzg">
              © 2026 NatuCafé. Todos los derechos reservados.
            </p>
            <div className="font-serif text-[#F5F0EB]/20 text-xl" data-id="element-7353" data-mp="tnhcy7tqsarn">N</div>
          </div>
        </div>
      </footer>
    </div>;
}